<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { EMPRESA_SOCIAL_PLATFORMS } from './socialPlatforms';

const props = defineProps({
  // When provided, the modal edits this empresa (PATCH). Otherwise it creates one (POST).
  empresa: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['created', 'updated']);

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);
const emptySocial = () =>
  EMPRESA_SOCIAL_PLATFORMS.reduce((acc, p) => ({ ...acc, [p.key]: '' }), {});
const form = ref({
  name: '',
  email_domain: '',
  website: '',
  phone_number: '',
  social: emptySocial(),
});

const socialPlatforms = EMPRESA_SOCIAL_PLATFORMS;

const isEdit = computed(() => Boolean(props.empresa?.id));
const uiFlags = computed(() => store.getters.getEmpresaUIFlags);
const isSaving = computed(() =>
  isEdit.value ? uiFlags.value.isUpdating : uiFlags.value.isCreating
);
const isNameValid = computed(() => Boolean(form.value.name.trim()));

const resetForm = () => {
  const profiles = props.empresa?.additional_attributes?.social_profiles || {};
  form.value = {
    name: props.empresa?.name || '',
    email_domain: props.empresa?.email_domain || '',
    website: props.empresa?.website || '',
    phone_number: props.empresa?.phone_number || '',
    social: EMPRESA_SOCIAL_PLATFORMS.reduce(
      (acc, p) => ({ ...acc, [p.key]: profiles[p.key] || '' }),
      {}
    ),
  };
};

watch(() => props.empresa, resetForm, { immediate: true });

const open = () => {
  resetForm();
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const submit = async () => {
  if (!isNameValid.value || isSaving.value) return;

  const socialProfiles = {};
  EMPRESA_SOCIAL_PLATFORMS.forEach(p => {
    const value = form.value.social[p.key]?.trim();
    if (value) socialProfiles[p.key] = value;
  });

  const payload = {
    name: form.value.name.trim(),
    email_domain: form.value.email_domain.trim(),
    website: form.value.website.trim(),
    phone_number: form.value.phone_number.trim(),
    additional_attributes: {
      ...(props.empresa?.additional_attributes || {}),
      social_profiles: socialProfiles,
    },
  };

  try {
    if (isEdit.value) {
      const updated = await store.dispatch('updateEmpresa', {
        id: props.empresa.id,
        ...payload,
      });
      useAlert(t('EMPRESAS.API.UPDATE_SUCCESS'));
      emit('updated', updated);
    } else {
      const created = await store.dispatch('createEmpresa', payload);
      useAlert(t('EMPRESAS.API.CREATE_SUCCESS'));
      emit('created', created);
    }
    close();
  } catch (error) {
    useAlert(t('EMPRESAS.API.ERROR'));
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="lg"
    :title="isEdit ? t('EMPRESAS.EDIT_TITLE') : t('EMPRESAS.CREATE_TITLE')"
    :confirm-button-label="
      isEdit ? t('EMPRESAS.ACTIONS.SAVE') : t('EMPRESAS.ACTIONS.CREATE')
    "
    :disable-confirm-button="!isNameValid"
    :is-loading="isSaving"
    @confirm="submit"
  >
    <div class="flex flex-col gap-5">
      <!-- Name (hero) -->
      <input
        v-model="form.name"
        type="text"
        :aria-label="t('EMPRESAS.FIELDS.NAME')"
        :placeholder="t('EMPRESAS.NAME_PLACEHOLDER')"
        class="w-full text-lg font-semibold leading-snug bg-transparent rounded-md reset-base text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:bg-n-alpha-1 focus:px-2 focus:-mx-2 focus:py-1"
        @keyup.enter="submit"
      />

      <!-- Email domain -->
      <div class="flex items-start gap-3 min-w-0">
        <span
          class="flex items-center gap-2 mt-1.5 text-sm w-28 shrink-0 text-n-slate-11"
        >
          <span class="i-lucide-globe text-n-slate-10" />
          {{ t('EMPRESAS.FIELDS.EMAIL_DOMAIN') }}
        </span>
        <div class="flex flex-col flex-1 min-w-0 gap-1">
          <input
            v-model="form.email_domain"
            type="text"
            :placeholder="t('EMPRESAS.PLACEHOLDERS.EMAIL_DOMAIN')"
            class="w-full h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
          />
          <p class="text-xs text-n-slate-10">
            {{ t('EMPRESAS.AUTO_LINK_HINT') }}
          </p>
        </div>
      </div>

      <!-- Website -->
      <div class="flex items-center gap-3 min-w-0">
        <span
          class="flex items-center gap-2 text-sm w-28 shrink-0 text-n-slate-11"
        >
          <span class="i-lucide-link text-n-slate-10" />
          {{ t('EMPRESAS.FIELDS.WEBSITE') }}
        </span>
        <input
          v-model="form.website"
          type="text"
          :placeholder="t('EMPRESAS.PLACEHOLDERS.WEBSITE')"
          class="flex-1 min-w-0 h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
        />
      </div>

      <!-- Phone -->
      <div class="flex items-center gap-3 min-w-0">
        <span
          class="flex items-center gap-2 text-sm w-28 shrink-0 text-n-slate-11"
        >
          <span class="i-lucide-phone text-n-slate-10" />
          {{ t('EMPRESAS.FIELDS.PHONE_NUMBER') }}
        </span>
        <input
          v-model="form.phone_number"
          type="tel"
          :placeholder="t('EMPRESAS.PLACEHOLDERS.PHONE_NUMBER')"
          class="flex-1 min-w-0 h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
        />
      </div>

      <!-- Social links -->
      <div class="flex flex-col gap-2 pt-1">
        <span
          class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
        >
          <span class="i-lucide-share-2" />
          {{ t('EMPRESAS.SOCIAL.TITLE') }}
        </span>
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div
            v-for="platform in socialPlatforms"
            :key="platform.key"
            class="relative min-w-0"
          >
            <span
              :class="platform.icon"
              class="absolute -translate-y-1/2 left-2.5 top-1/2 text-n-slate-10 size-4"
            />
            <input
              v-model="form.social[platform.key]"
              type="text"
              :aria-label="platform.label"
              :placeholder="platform.label"
              class="w-full h-8 pl-8 pr-3 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
            />
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
